import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { LayoutTemplate, Plus, Pencil, Eye, Trash2, Globe, EyeOff } from "lucide-react";
import { toast } from "sonner";
import api, { apiErrorMessage } from "@/lib/api";

export default function AdminLandingPages() {
  const [pages, setPages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [confirmDelete, setConfirmDelete] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/landing-pages");
      setPages(r.data || []);
    } catch (e) {
      toast.error(apiErrorMessage(e));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const togglePublish = async (p) => {
    try {
      const publish = p.status !== "published";
      await api.patch(`/admin/landing-pages/${p.id}/publish`, { publish });
      toast.success(publish ? "Published" : "Unpublished");
      load();
    } catch (e) {
      toast.error(apiErrorMessage(e));
    }
  };

  const doDelete = async (p) => {
    try {
      await api.delete(`/admin/landing-pages/${p.id}`);
      toast.success("Landing page deleted");
      setConfirmDelete(null);
      load();
    } catch (e) {
      toast.error(apiErrorMessage(e));
    }
  };

  return (
    <div data-testid="admin-landing-pages">
      <div className="mb-6 flex items-start justify-between gap-4">
        <div>
          <h2 className="font-display font-bold text-2xl text-[#0F172A] flex items-center gap-2">
            <LayoutTemplate className="w-6 h-6 text-[#1E3A8A]" /> Landing Pages
          </h2>
          <p className="mt-1 text-sm text-[#475569]">
            Create SEO service landing pages. Published pages appear at their public URL and in the sitemap.
          </p>
        </div>
        <Link
          to="/admin/landing-pages/new"
          data-testid="lp-new"
          className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-[#1E3A8A] text-white font-semibold text-sm hover:bg-[#1E40AF] whitespace-nowrap"
        >
          <Plus className="w-4 h-4" /> New Landing Page
        </Link>
      </div>

      {loading ? (
        <p className="text-sm text-[#475569]">Loading…</p>
      ) : pages.length === 0 ? (
        <div className="bg-white rounded-2xl border border-slate-100 shadow-soft p-10 text-center">
          <LayoutTemplate className="w-10 h-10 mx-auto text-slate-300 mb-3" />
          <p className="text-[#475569]">No landing pages yet. Create your first one.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {pages.map((p) => (
            <div key={p.id} data-testid={`lp-row-${p.slug}`} className="bg-white rounded-2xl border border-slate-100 shadow-soft p-4 flex flex-wrap items-center justify-between gap-3">
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <p className="font-display font-semibold text-[#0F172A] truncate">{p.name}</p>
                  <span className={`text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded-full ${p.status === "published" ? "bg-green-100 text-green-700" : "bg-slate-100 text-slate-500"}`}>
                    {p.status}
                  </span>
                  {p.index_page === false && (
                    <span className="text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded-full bg-amber-100 text-amber-700">noindex</span>
                  )}
                </div>
                <p className="text-xs text-[#475569] mt-0.5">/{p.slug}</p>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <a href={`/${p.slug}?preview=${p.id}`} target="_blank" rel="noreferrer" data-testid={`lp-preview-${p.slug}`} className="inline-flex items-center gap-1.5 px-3 py-2 text-sm rounded-lg border border-slate-200 hover:bg-slate-50">
                  <Eye className="w-4 h-4" /> Preview
                </a>
                <button onClick={() => togglePublish(p)} data-testid={`lp-publish-${p.slug}`} className="inline-flex items-center gap-1.5 px-3 py-2 text-sm rounded-lg border border-slate-200 hover:bg-slate-50">
                  {p.status === "published" ? <><EyeOff className="w-4 h-4" /> Unpublish</> : <><Globe className="w-4 h-4" /> Publish</>}
                </button>
                <Link to={`/admin/landing-pages/${p.id}`} data-testid={`lp-edit-${p.slug}`} className="inline-flex items-center gap-1.5 px-3 py-2 text-sm rounded-lg border border-slate-200 hover:bg-slate-50">
                  <Pencil className="w-4 h-4" /> Edit
                </Link>
                <button onClick={() => setConfirmDelete(p)} data-testid={`lp-delete-${p.slug}`} className="inline-flex items-center gap-1.5 px-3 py-2 text-sm rounded-lg border border-rose-200 text-rose-700 hover:bg-rose-50">
                  <Trash2 className="w-4 h-4" /> Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {confirmDelete && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" data-testid="lp-delete-modal">
          <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-6">
            <h3 className="font-display font-bold text-lg text-[#0F172A]">Delete landing page?</h3>
            <p className="mt-2 text-sm text-[#475569]">
              This will permanently delete <strong>{confirmDelete.name}</strong> (/{confirmDelete.slug}). This cannot be undone.
            </p>
            <div className="mt-6 flex justify-end gap-3">
              <button onClick={() => setConfirmDelete(null)} className="px-4 py-2 text-sm rounded-lg border border-slate-200 hover:bg-slate-50">Cancel</button>
              <button onClick={() => doDelete(confirmDelete)} data-testid="lp-delete-confirm" className="px-4 py-2 text-sm rounded-lg bg-rose-600 text-white font-semibold hover:bg-rose-700">Delete</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
